import LumaResourceWorkspace, {lumaMoney} from '../../components/admin/LumaResourceWorkspace';
// NivelUsuariosPage.tsx — Co Marca Admin — estilo CategoryPage
import Loading from '../../components/Loading';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Edit, Trash2, Shield, Plus, ChevronRight, Home } from 'lucide-react';
import UserLevelModal from '../../components/Userslevel/UserLevelModal';
import EditUserLevelModal from '../../components/Userslevel/EditUserLevelModal';
import DeleteConfirmationModal from '../../components/Userslevel/DeleteConfirmationModal';
import Pagination from '../../components/Pagination/Pagination';
import ExpiredSessionModal from '../../components/Expired/ExpiredSessionModal';
import { fetchUserLevels } from '../../services/userLevelService';
import { useSessionExpiration } from '../../hooks/useSessionExpiration';
import { useAuth } from '../../context/AuthContext';
import { useModelPermission } from '../../hooks/useModelPermission';
import { usePermissions } from '../../context/PermissionsContext';
import { canAccessRoute } from '../../utils/routeAccess';

const colors = {
    primary:        'var(--ad-primary, #3564db)',
    primaryDark:    'var(--ad-primary-dark, #8f1f64)',
    secondary:      'var(--ad-secondary, #3564db)',
    secondaryLight: 'var(--ad-secondary, #92b1ed)',
    accent:         '#ffd97c',
    accentDark:     '#ea8406',
};

const css = `

.nup-page { font-family:'DM Sans',sans-serif; }
@keyframes nupFadeUp { from{opacity:0;transform:translateY(18px);}to{opacity:1;transform:translateY(0);} }
.nup-fadeup   { animation:nupFadeUp .5s cubic-bezier(.16,1,.3,1) both; }
.nup-fadeup-1 { animation:nupFadeUp .5s .08s cubic-bezier(.16,1,.3,1) both; }
.nup-btn-primary {
    display:inline-flex;align-items:center;gap:7px;
    background:linear-gradient(135deg,${colors.primary},${colors.primaryDark});
    color:#fff;border:none;border-radius:11px;
    padding:9px 20px;font-size:.83rem;font-weight:800;
    cursor:pointer;font-family:'DM Sans',sans-serif;
    box-shadow:0 4px 14px rgba(var(--ad-primary-rgb, 193, 52, 139),.25);
    transition:all .22s cubic-bezier(.16,1,.3,1);white-space:nowrap;
}
.nup-btn-primary:hover{transform:translateY(-2px);box-shadow:0 8px 22px rgba(var(--ad-primary-rgb, 193, 52, 139),.32);}
.nup-table-row{transition:background .15s;}
.nup-table-row:hover{background:var(--ad-soft, #fafcff);}
.nup-action-btn{width:30px;height:30px;border-radius:9px;border:none;display:inline-flex;align-items:center;justify-content:center;cursor:pointer;transition:all .2s;}
.custom-scroll::-webkit-scrollbar{height:6px;width:6px;}
.custom-scroll::-webkit-scrollbar-track{background:transparent;}
.custom-scroll::-webkit-scrollbar-thumb{background-color:rgba(97, 37, 123,0.3);border-radius:20px;}
.custom-scroll::-webkit-scrollbar-thumb:hover{background-color:rgba(97, 37, 123,0.5);}
`;

const NivelUsuariosPage: React.FC = () => {
    const { isSessionExpired, setIsSessionExpired } = useSessionExpiration();
    const { user } = useAuth();
    const userLevelId = user?.user_level_id ?? 0;
    const perm = useModelPermission('userlevel', userLevelId);
    const permissionsModel = useModelPermission('modelpermission', userLevelId);
    const { permissions, disabledPaths } = usePermissions();
    const navigate = useNavigate();

    const [isModalOpen, setIsModalOpen]         = useState(false);
    const [userLevels, setUserLevels]           = useState<any[]>([]);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [currentPage, setCurrentPage]         = useState(1);
    const itemsPerPage = 10;
    const [editModalOpen, setEditModalOpen]     = useState(false);
    const [selectedLevel, setSelectedLevel]     = useState<any>(null);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [levelToDelete, setLevelToDelete]     = useState<any>(null);

    const loadUserLevels = useCallback(async (newLevels?: any) => {
        if (!perm?.can_read) return;
        setLoadError(null);
        try {
            if (newLevels) setUserLevels(newLevels.sort((a: any, b: any) => a.id - b.id));
            else { const data = await fetchUserLevels(); setUserLevels(data.sort((a: any, b: any) => a.id - b.id)); }
        } catch (e: any) {
            setUserLevels([]);
            setLoadError(e?.response?.status === 403 ? 'Tu nivel no tiene autorización para consultar los niveles de usuario.' : 'No se pudieron cargar los niveles de usuario. Vuelve a intentarlo.');
        }
    }, [perm]);

    useEffect(() => { if (perm) loadUserLevels(); }, [perm, loadUserLevels]);

    const sortedLevels    = useMemo(() => [...userLevels].sort((a,b)=>a.id-b.id), [userLevels]);
    const totalPages      = Math.ceil(sortedLevels.length / itemsPerPage);
    const displayedLevels = sortedLevels.slice((currentPage-1)*itemsPerPage, currentPage*itemsPerPage);

    if (perm === null) return <Loading text="Cargando niveles de usuarios..." />;

    return <><LumaResourceWorkspace title="Nivel de usuarios" description="Consulta y gestiona la información de tu coworking." group="Usuarios" rows={userLevels} columns={[{key:'nombre',label:'Nombre del nivel'},{key:'perfil_asociado',label:'Perfil asociado'},{key:'activo',label:'Estado'}]} idKey="id" nameKey="nombre" statusKey="activo" canRead={perm?.can_read} onCreate={perm?.can_create?()=>{setIsModalOpen(true)}:undefined} onEdit={perm?.can_update?r=>{setSelectedLevel(r);setEditModalOpen(true);}:undefined} onDelete={perm?.can_delete?r=>{setLevelToDelete(r);setDeleteModalOpen(true);}:undefined} extraActions={r=><>{permissionsModel?.can_read&&canAccessRoute(`/userlevels/${r.id}/userlevelpermisos`,user?.role,permissions,disabledPaths)&&<button type="button" onClick={()=>navigate(`/userlevels/${r.id}/userlevelpermisos`)}>Permisos</button>}</>}/><ExpiredSessionModal isOpen={isSessionExpired} onClose={()=>{ setIsSessionExpired(false); window.location.href='/login'; }}/><UserLevelModal isOpen={isModalOpen} onClose={()=>setIsModalOpen(false)} addUserLevel={l=>setUserLevels(p=>[...p,l].sort((a,b)=>a.id-b.id))}/><EditUserLevelModal isOpen={editModalOpen} onClose={()=>setEditModalOpen(false)} userLevel={selectedLevel} onSave={()=>{ loadUserLevels(); setEditModalOpen(false); }}/><DeleteConfirmationModal isOpen={deleteModalOpen} onClose={()=>setDeleteModalOpen(false)} user={levelToDelete} onDeleted={()=>{ loadUserLevels(); setDeleteModalOpen(false); }}/></>;
};

export default NivelUsuariosPage;


